import { log } from "utils/LogUtil";
import { LogLevel } from "utils/LogUtil";
import { TestExperiment, TestStatus } from "types/aiReviewTest";

export interface TestWorkerMessage {
  type: "start" | "progress" | "complete" | "error" | "log";
  experimentId: number;
  data?: any;
  error?: string;
}

export interface TestProgress {
  experimentId: number;
  currentItemIndex: number;
  totalItems: number;
  currentItem: string;
  status: "running" | "completed" | "failed";
  message?: string;
}

// 스트림 이벤트 타입
export type EvaluationStreamProgressCallback = (progress: TestProgress) => void;
export type EvaluationStreamStatusCallback = (status: TestStatus, message: string) => void;
export type EvaluationStreamErrorCallback = (error: any) => void;
export type EvaluationStreamCompleteCallback = (experiment: TestExperiment) => void;
export type EvaluationStreamLogCallback = (log: string) => void;

export class AiReviewEvaluationStreamer {
  onProgress: EvaluationStreamProgressCallback;
  onStatus: EvaluationStreamStatusCallback;
  onComplete: EvaluationStreamCompleteCallback;
  onError: EvaluationStreamErrorCallback;
  onLog?: EvaluationStreamLogCallback;
  experimentId: number = 0;
  eventSource: EventSource | null = null;

  constructor(
    experimentId: number,
    onProgress: EvaluationStreamProgressCallback,
    onStatus: EvaluationStreamStatusCallback,
    onComplete: EvaluationStreamCompleteCallback,
    onError: EvaluationStreamErrorCallback,
    onLog?: EvaluationStreamLogCallback
  ) {
    log(LogLevel.INFO, "AiReviewEvaluationStreamer:생성자 호출", experimentId);
    this.experimentId = experimentId;
    this.onProgress = onProgress;
    this.onStatus = onStatus;
    this.onComplete = onComplete;
    this.onError = onError;
    this.onLog = onLog;

    const url =
      (process.env.NODE_ENV === "production" ? process.env.REACT_APP_BACKEND_URL : "") +
      `/api/v1/admin/ai-review-evaluation/experiments/${this.experimentId}/stream`;
    log(LogLevel.INFO, "EventSource URL:", url);

    this.eventSource = new EventSource(url, {
      withCredentials: true,
    });

    log(LogLevel.INFO, "EventSource 생성됨, readyState:", this.eventSource.readyState);

    this.eventSource.onopen = (event) => {
      log(
        LogLevel.INFO,
        "AiReviewEvaluationStreamer:연결됨",
        this.experimentId,
        "readyState:",
        this.eventSource?.readyState
      );
    };

    this.eventSource.onmessage = this._onMessage;
    this.eventSource.onerror = this._onError;
  }

  /**
   * 스트리머 연결을 종료하고 리소스를 정리합니다.
   */
  close() {
    log(LogLevel.INFO, `AiReviewEvaluationStreamer:close 호출됨 - experimentId: ${this.experimentId}`);

    // 콜백 함수들 정리
    this.onProgress = null as any;
    this.onStatus = null as any;
    this.onComplete = null as any;
    this.onError = null as any;
    this.onLog = null as any;

    if (this.eventSource) {
      log(LogLevel.VERBOSE, `EventSource 종료 중 - readyState: ${this.eventSource.readyState}`);
      this.eventSource.close();
      this.eventSource = null;
      log(LogLevel.VERBOSE, `EventSource 종료 완료`);
    }

    evaluationStreamPool.delete(this.experimentId);

    log(LogLevel.INFO, `AiReviewEvaluationStreamer:리소스 정리 완료 - experimentId: ${this.experimentId}`);
  }

  private _onMessage = (event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      log(LogLevel.INFO, "AiReviewEvaluationStreamer:_onMessage", this.experimentId, message);
      
      switch (message.type) {
        case "status":
          // 상태 업데이트
          if (this.onStatus && message.status) {
            this.onStatus(message.status, message.message || "");
          }
          // status가 RUNNING이 아닌 경우 스트림 종료
          if (message.status !== TestStatus.RUNNING) {
            this.close();
          }
          break;
          
        case "progress":
          // 진행 상황 업데이트
          if (this.onProgress) {
            const progressData: TestProgress = {
              experimentId: message.experimentId,
              currentItemIndex: message.currentItemIndex,
              totalItems: message.totalItems,
              currentItem: message.currentItem,
              status: message.status,
              message: message.message
            };
            this.onProgress(progressData);
            
            // completed 상태인 경우 onComplete 호출
            if (message.status === "completed" && this.onComplete) {
              this.onComplete({} as TestExperiment);
              this.close();
            }
          }
          break;
          
        case "error":
          // 에러 발생
          if (this.onError) {
            this.onError(message.error || message.message || "알 수 없는 오류가 발생했습니다.");
          }
          if (this.onStatus) {
            this.onStatus(TestStatus.FAILED, message.error || message.message || "오류가 발생했습니다.");
          }
          this.close();
          break;
          
        case "log":
          // 로그 메시지
          if (this.onLog && message.data) {
            this.onLog(message.data);
          }
          break;
          
        default:
          log(LogLevel.WARNING, "Unknown message type:", message.type, message);
      }
    } catch (error) {
      log(LogLevel.ERROR, "AiReviewEvaluationStreamer:_onMessage 에러 발생", error);
    }
  };

  private _onError = (err: Event) => {
    log(LogLevel.ERROR, "AiReviewEvaluationStreamer:_onError 호출됨", {
      experimentId: this.experimentId,
      readyState: this.eventSource?.readyState,
      url: this.eventSource?.url,
      error: err,
    });

    // EventSource readyState 정보
    // 0: CONNECTING, 1: OPEN, 2: CLOSED
    const readyStateText =
      this.eventSource?.readyState === 0
        ? "CONNECTING"
        : this.eventSource?.readyState === 1
        ? "OPEN"
        : "CLOSED";
    log(LogLevel.ERROR, `EventSource 상태: ${readyStateText} (${this.eventSource?.readyState})`);

    this.close();
    if (this.onError) {
      this.onError(err);
    }
  };
}

export const evaluationStreamPool = new Map<number, AiReviewEvaluationStreamer>();

/**
 * AI Review 평가 실험 실시간 스트림 API
 * Server-Sent Events(SSE)를 사용하여 실시간으로 실험 진행 상황을 스트리밍 받습니다.
 *
 * @param experimentId 실험 ID
 * @param onProgress 진행 상황 업데이트 시 호출될 콜백 함수
 * @param onStatus 상태 변경 시 호출될 콜백 함수
 * @param onComplete 실험이 완료되었을 때 호출될 콜백 함수
 * @param onError 에러 발생 시 호출될 콜백 함수
 * @param onLog 로그 메시지 수신 시 호출될 콜백 함수
 * @returns void
 */
export const connectEvaluationStream = (
  experimentId: number,
  onProgress: EvaluationStreamProgressCallback,
  onStatus: EvaluationStreamStatusCallback,
  onComplete: EvaluationStreamCompleteCallback,
  onError?: EvaluationStreamErrorCallback,
  onLog?: EvaluationStreamLogCallback
) => {
  log(LogLevel.INFO, `connectEvaluationStream 호출됨 - experimentId: ${experimentId}`);

  try {
    // SSE 연결을 위한 EventSource 생성
    log(LogLevel.VERBOSE, `스트리머 풀에서 검색 중 - experimentId: ${experimentId}`);
    let streamer = evaluationStreamPool.get(experimentId);
    log(LogLevel.VERBOSE, `스트리머 검색 결과:`, streamer ? `존재함 (id: ${streamer.experimentId})` : "없음");

    if (!streamer) {
      log(LogLevel.INFO, `새로운 AiReviewEvaluationStreamer 생성 - experimentId: ${experimentId}`);
      log(LogLevel.VERBOSE, `AiReviewEvaluationStreamer 생성자 호출 직전`);
      streamer = new AiReviewEvaluationStreamer(
        experimentId, 
        onProgress, 
        onStatus, 
        onComplete, 
        onError || (() => {}),
        onLog
      );
      log(LogLevel.VERBOSE, `AiReviewEvaluationStreamer 생성자 호출 완료`);
      evaluationStreamPool.set(experimentId, streamer);
      log(LogLevel.VERBOSE, `스트리머 풀에 추가됨 - 현재 풀 크기: ${evaluationStreamPool.size}`);
    } else {
      log(LogLevel.INFO, `기존 AiReviewEvaluationStreamer 재사용 - experimentId: ${experimentId}`);
      streamer.onProgress = onProgress;
      streamer.onStatus = onStatus;
      streamer.onComplete = onComplete;
      streamer.onError = onError || (() => {});
      streamer.onLog = onLog;
      log(LogLevel.VERBOSE, `스트리머 콜백 함수들 업데이트 완료`);
    }

    log(LogLevel.VERBOSE, `connectEvaluationStream 완료 - experimentId: ${experimentId}`);
    return;
  } catch (error) {
    log(LogLevel.ERROR, `connectEvaluationStream 에러 발생 - experimentId: ${experimentId}`, error);
    if (onError) {
      onError(error);
    }
    return;
  }
};

/**
 * AI Review 평가 실험 실시간 스트림을 중단합니다.
 * 해당 실험 ID의 스트리머 연결을 종료하고 풀에서 제거합니다.
 *
 * @param experimentId 실험 ID
 */
export const disconnectEvaluationStream = (experimentId: number) => {
  log(LogLevel.INFO, `disconnectEvaluationStream 호출됨 - experimentId: ${experimentId}`);
  
  const streamer = evaluationStreamPool.get(experimentId);
  if (streamer) {
    log(LogLevel.INFO, `스트리머 찾음, close 호출 - experimentId: ${experimentId}`);
    streamer.close();
  } else {
    log(LogLevel.WARNING, `스트리머를 찾을 수 없음 - experimentId: ${experimentId}`);
  }
};

/**
 * 모든 AI Review 평가 실험 스트림 연결을 종료합니다.
 */
export const disconnectAllEvaluationStreams = () => {
  log(LogLevel.INFO, `disconnectAllEvaluationStreams 호출됨 - 풀 크기: ${evaluationStreamPool.size}`);
  
  evaluationStreamPool.forEach((streamer, experimentId) => {
    log(LogLevel.INFO, `스트리머 종료 중 - experimentId: ${experimentId}`);
    streamer.close();
  });
  
  evaluationStreamPool.clear();
  log(LogLevel.INFO, `모든 스트리머 종료 완료`);
};